import { useState, useEffect } from "react";
import axios from "axios";
import { Eye, EyeOff, CheckCircle, XCircle, Loader } from "lucide-react";

// "" (empty) is a valid, intentional value: the production build uses relative
// /api URLs behind Caddy (same origin). `||` would treat "" as unset and send
// the browser to localhost:8001; `??` only falls back when it is truly unset.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL ?? "http://localhost:8001";

export default function ClientActivation() {
  const [token, setToken] = useState("");
  const [step, setStep] = useState("validating"); // validating, set_password, success, error
  const [tokenData, setTokenData] = useState(null);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Extract token from URL
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    
    if (!urlToken) {
      setStep("error");
      setError("No activation token provided");
      return;
    }
    
    setToken(urlToken);
    validateToken(urlToken);
  }, []);

  const validateToken = async (token) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/client/validate-token`, { token });
      setTokenData(res.data);
      setStep("set_password");
    } catch (err) {
      setStep("error");
      setError(err.response?.data?.detail || "Invalid or expired activation token");
    }
  };

  const handleActivate = async (e) => {
    e.preventDefault();
    
    // Mirror the backend's validate_password_complexity() so a weak password
    // is caught here instead of spending one of the 3/hour activation attempts.
    const problems = [];
    if (password.length < 8) problems.push("at least 8 characters");
    if (!/[A-Z]/.test(password)) problems.push("an uppercase letter");
    if (!/[a-z]/.test(password)) problems.push("a lowercase letter");
    if (!/[0-9]/.test(password)) problems.push("a number");
    if (problems.length) {
      setError(`Password must contain ${problems.join(", ")}`);
      return;
    }
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const res = await axios.post(`${BACKEND_URL}/api/client/activate`, {
        token,
        password
      });
      
      // Store token and redirect to dashboard
      localStorage.setItem("client_token", res.data.token);
      setStep("success");
      
      // Redirect after 2 seconds
      setTimeout(() => {
        window.location.href = "/client/dashboard";
      }, 2000);
      
    } catch (err) {
      // The complexity check returns detail as {message, errors: [...]} and a
      // 422 returns a list - rendering either object directly as {error}
      // crashed the page ("Objects are not valid as a React child").
      const detail = err.response?.data?.detail;
      let message = detail;
      if (Array.isArray(detail)) {
        message = detail.map((item) => item?.msg || String(item)).join(", ");
      } else if (detail && typeof detail === "object") {
        message = (detail.errors || []).join(", ") || detail.message;
      }
      setError(message || "Activation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (step === "validating") {
    return (
      <div className="login-container" data-testid="client-activation-validating">
        <div className="login-panel glass-panel">
          <div className="brand-lockup" style={{marginBottom: "2rem"}}>
            <h2>MedSpaBookings</h2>
            <span>Account Activation</span>
          </div>
          <div style={{textAlign: "center", padding: "2rem 0"}}>
            <Loader size={48} className="spin" style={{margin: "0 auto", color: "var(--gold)"}}/>
            <p style={{marginTop: "1rem"}}>Validating your activation link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (step === "error") {
    return (
      <div className="login-container" data-testid="client-activation-error">
        <div className="login-panel glass-panel">
          <div className="brand-lockup" style={{marginBottom: "2rem"}}>
            <h2>MedSpaBookings</h2>
            <span>Account Activation</span>
          </div>
          <div style={{textAlign: "center", padding: "2rem 0"}}>
            <XCircle size={64} style={{color: "var(--error)", margin: "0 auto"}}/>
            <h3 style={{marginTop: "1rem", color: "var(--error)"}}>Activation Failed</h3>
            <p style={{marginTop: "0.5rem", color: "var(--text-secondary)"}}>{error}</p>
            <p style={{marginTop: "2rem", fontSize: "0.875rem"}}>
              Need help? Contact your account manager or email support@medspabookings.co
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (step === "success") {
    return (
      <div className="login-container" data-testid="client-activation-success">
        <div className="login-panel glass-panel">
          <div className="brand-lockup" style={{marginBottom: "2rem"}}>
            <h2>MedSpaBookings</h2>
            <span>Account Activation</span>
          </div>
          <div style={{textAlign: "center", padding: "2rem 0"}}>
            <CheckCircle size={64} style={{color: "var(--success)", margin: "0 auto"}}/>
            <h3 style={{marginTop: "1rem", color: "var(--success)"}}>Account Activated!</h3>
            <p style={{marginTop: "0.5rem", color: "var(--text-secondary)"}}>
              Your account has been activated successfully.
            </p>
            <p style={{marginTop: "1rem"}}>Redirecting to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Set Password Form
  return (
    <div className="login-container" data-testid="client-activation-form">
      <div className="login-panel glass-panel">
        <div className="brand-lockup" style={{marginBottom: "2rem"}}>
          <h2>MedSpaBookings</h2>
          <span>Activate Your Account</span>
        </div>
        
        {tokenData && (
          <div style={{marginBottom: "1.5rem", padding: "1rem", background: "var(--bg-secondary)", borderRadius: "6px"}}>
            <p style={{margin: 0, fontWeight: 600}}>{tokenData.clinic_name}</p>
            <p style={{margin: "0.25rem 0 0 0", fontSize: "0.875rem", color: "var(--text-secondary)"}}>
              {tokenData.email}
            </p>
          </div>
        )}
        
        <form onSubmit={handleActivate}>
          <label>
            Create Password
            <div style={{position: "relative"}}>
              <input 
                type={showPassword ? "text" : "password"}
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                required 
                minLength={8}
                placeholder="Minimum 8 characters"
                data-testid="client-activation-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "var(--text-secondary)"
                }}
                data-testid="client-activation-toggle-password"
              >
                {showPassword ? <EyeOff size={18}/> : <Eye size={18}/>}
              </button>
            </div>
          </label>
          
          <label>
            Confirm Password
            <input 
              type={showPassword ? "text" : "password"}
              value={confirmPassword} 
              onChange={(e) => setConfirmPassword(e.target.value)} 
              required 
              minLength={8}
              placeholder="Re-enter your password"
              data-testid="client-activation-confirm-password"
            />
          </label>
          
          {error && (
            <div style={{
              padding: "0.75rem",
              background: "var(--error-bg)",
              border: "1px solid var(--error)",
              borderRadius: "6px",
              color: "var(--error)",
              marginBottom: "1rem",
              fontSize: "0.875rem"
            }} data-testid="client-activation-error-message">
              {error}
            </div>
          )}
          
          <p style={{marginTop: "0.75rem", fontSize: "0.8rem", color: "var(--text-secondary)"}} data-testid="client-activation-legal-notice">
            By activating your account you agree to the{" "}
            <a href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and{" "}
            <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.
          </p>

          <button
            type="submit"
            className="primary-btn"
            disabled={loading}
            data-testid="client-activation-submit"
          >
            {loading ? "Activating..." : "Activate Account"}
          </button>
        </form>
        
        <div style={{marginTop: "1.5rem", padding: "1rem", background: "var(--bg-secondary)", borderRadius: "6px", fontSize: "0.875rem"}}>
          <p style={{margin: 0, fontWeight: 600, marginBottom: "0.5rem"}}>Password Requirements:</p>
          <ul style={{margin: 0, paddingLeft: "1.5rem"}}>
            <li>At least 8 characters</li>
            <li>At least one uppercase and one lowercase letter</li>
            <li>At least one number</li>
          </ul>
        </div>
        
        <p style={{marginTop: "1.5rem", fontSize: "0.875rem", color: "var(--text-secondary)", textAlign: "center"}}>
          Need help? Contact support@medspabookings.co
        </p>
      </div>
    </div>
  );
}
